import { Box, Button, CircularProgress, MobileStepper } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import DataGrid from 'components/data-grid';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import FlexBox from 'components/flex-box';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { isValidDecimal, isValidMeanWeight } from 'utils/validate-numbers';
import { postSampling } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { CreateSamplingInput } from 'views/production-systems/features/farm-overview/types/create-sampling-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Pond } from 'views/production-systems/types/pond.type';
import { Sampling } from 'views/production-systems/types/sampling.type';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import { getCycleName } from 'views/production-systems/utils/get-cycle-name';
import RegistrationHeader from '../../../../components/registration-header';
import ListSelectedPonds from '../list-selected-ponds';
import DuplicateRegistrationWarning, { REGISTRATION_EXISTS_QUERY_KEY } from '../duplicate-registration-warning';
import { RegistrationActionType } from '../../types/registration-action-type.enum';

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

type Row = {
  pondId: string;
  pondName: string;
  cycleId: number;
  zoneName: string;
  cycleName: string;
  currentMw: number;
  currentQty: number;
  currentBm: number;
  newMw: number;
  survivalRate: number;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.sampling';
const COSTAMARKET_COMPANY_ID = 'b3f2a71f-bbf5-42fa-b040-918bd43a9373';

const AddSampling: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError, setIsEditing } = useDataGridError();

  const {
    account: { company },
  } = useAccountState();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);
  const survivalRatePercentage = 150;
  //#region Data Grid

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => ({
      pondName: pond.pondName,
      pondId: pond.pondId,
      cycleName: pond.cycleName,
      cycleId: pond.cycleId,
      zoneName: pond.zoneName,
      currentMw: pond.currentMw,
      currentQty: pond.currentQty,
      currentBm: calculateBiomass(pond.currentMw, pond.currentQty),
      newMw: 0,
      survivalRate: 0,
    }));
  }, [selectedPonds]);

  const [rows, setRows] = useState(initialRows || []);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow };
    setRows((prevRows) => prevRows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    setIsEditing(false);
    return updatedRow;
  };

  /**
   * This column should be hidden if the company is COSTAMARKET, a shrimp customer in Production, as they do not use survival rate in bluViu
   */
  const survivalRateColumn: GridColDef = useMemo(() => {
    return {
      field: 'survivalRate',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.survivalRate` }),
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      type: 'number',
      valueFormatter: (value: number) => `${value}%`,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError =
          !isValidDecimal(params.props.value) || Number(params.props.value) < 1 || Number(params.props.value) > survivalRatePercentage;
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-survivalRate`]: hasError });
        return { ...params.props, error: hasError };
      },
    };
  }, [company]);

  const columns: GridColDef[] = [
    {
      field: 'pondName',
      flex: 2,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.destinationPond` }),
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              width: '100%',
            }}>
            {!!row.cycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={row.pondName}>
              {row.pondName}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'cycleName',
      flex: 1,
      editable: false,
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.cycleName` }),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'zoneName',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.zone` }),
      type: 'string',
      editable: false,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'currentQty',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.number` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
      flex: 1,
    },
    {
      field: 'currentMw',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.meanWeight` }),
      sortable: false,
      editable: false,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
    },
    {
      field: 'currentBm',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.biomass` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: false,
      flex: 1,
    },
    {
      field: 'newMw',
      headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.newMeanWeight` }),
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const value = Number(params.props.value);
        const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
        changeErrorDictionary({ ...errorDictionary, [`${params.id}-newMw`]: hasError });
        return { ...params.props, error: hasError };
      },
    },
    ...(company!.companyId !== COSTAMARKET_COMPANY_ID ? [survivalRateColumn] : []), // Hide for COSTAMARKET customer
  ] as GridColDef[];

  //#endregion Data Grid

  //#region DATA GRID

  const summaryValues: SummaryItem[] = useMemo(() => {
    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty);

        return {
          survivalRate: prev.survivalRate + curr.currentQty,
          currentQty: prev.currentQty + curr.currentQty,
          currentBm: prev.currentBm + biomass,
          currentMw: 0,
          newMw: 0,
        };
      },
      {
        survivalRate: 0,
        currentQty: 0,
        currentMw: 0,
        currentBm: 0,
        newMw: 0,
      }
    );

    newSummary.currentMw = calculateMeanWeight(newSummary.currentBm, newSummary.currentQty);

    return [
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.currentQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentMw,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentBm,
        type: 'number',
        hidden: false,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      ...(company!.companyId !== COSTAMARKET_COMPANY_ID
        ? [
          {
            value: undefined,
            type: undefined,
            hidden: true,
          },
        ]
        : []),
    ];
  }, [rows, company]);

  //#endregion DATA GRID

  //#region HANDLERS

  const handleSubmit = () => {
    let d: CreateSamplingInput = {
      date: registrationDate.format('YYYY-MM-DD'),
      cycles: rows.map((row) => ({
        cycleId: row.cycleId,
        survivalRate: company!.companyId !== COSTAMARKET_COMPANY_ID ? row.survivalRate : 100, // COSTAMARKET should always be 100% survival rate
        weight: row.newMw,
        pondId: row.pondId,
      })),
    };

    createSampling.mutate({ globalSiteId, data: d });
  };

  //#endregion HANDLERS

  //#region POST DATA

  const createSampling = usePost<Sampling, { globalSiteId: string; data: CreateSamplingInput }>(
    (request) => postSampling(request.globalSiteId, request.data),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region HOOKS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      const survivalRate = Number(row.survivalRate);

      const invalidNewMw = !isValidDecimal(row.newMw);
      const invalidSurvivalRate = !isValidDecimal(row.survivalRate) || survivalRate < 1 || survivalRate > survivalRatePercentage;

      // If company is COSTAMARKET, ignore survivalRate validation
      return company!.companyId === COSTAMARKET_COMPANY_ID ? invalidNewMw : invalidNewMw || invalidSurvivalRate;
    });
  }, [rows, company, survivalRatePercentage]);

  //#endregion HOOKS

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
        </FlexBox>
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.MeanweightSample} ponds={selectedPonds} />
          <RegistrationHeader />
          <DataGrid
            columns={columns}
            rows={rows}
            apiRef={apiRef}
            onCellEditStop={handleEditing}
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            showToolbar
            slots={{
              footer: DataGridSummary,
              toolbar: DataGridToolbar,
            }}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.meanweightSample',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'pondId', sort: 'asc' }],
              },
            }}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
          />
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createSampling.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createSampling.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

export default AddSampling;
